import React, { useEffect, useRef, useState } from 'react';
import styles from './CrosshairCursor.module.css';

export default function CrosshairCursor() {
  const dotRef    = useRef(null);
  const ringRef   = useRef(null);
  const cross1Ref = useRef(null); // horizontal line
  const cross2Ref = useRef(null); // vertical line
  const pos       = useRef({ x: -200, y: -200 });
  const ringPos   = useRef({ x: -200, y: -200 });
  const raf       = useRef(null);
  const [visible, setVisible]   = useState(false);
  const [hovering, setHovering] = useState(false);
  const [clicking, setClicking] = useState(false);

  useEffect(() => {
    document.body.style.cursor = 'none';

    const move = (e) => {
      pos.current = { x: e.clientX, y: e.clientY };
      if (!visible) setVisible(true);
    };

    const over = (e) => {
      const isLink = e.target.closest('a, button, [role="button"], .projStack, label');
      setHovering(!!isLink);
    };

    const down = () => setClicking(true);
    const up   = () => setClicking(false);
    const leave = () => setVisible(false);
    const enter = () => setVisible(true);

    document.addEventListener('mousemove', move);
    document.addEventListener('mouseover', over);
    document.addEventListener('mousedown', down);
    document.addEventListener('mouseup', up);
    document.addEventListener('mouseleave', leave);
    document.addEventListener('mouseenter', enter);

    const animate = () => {
      // Ring lags behind cursor with smooth lerp
      const lag = hovering ? 0.1 : 0.14;
      ringPos.current.x += (pos.current.x - ringPos.current.x) * lag;
      ringPos.current.y += (pos.current.y - ringPos.current.y) * lag;

      const rx = ringPos.current.x;
      const ry = ringPos.current.y;
      const cx = pos.current.x;
      const cy = pos.current.y;

      // Dot — snaps exactly to cursor
      if (dotRef.current) {
        dotRef.current.style.transform = `translate(${cx}px, ${cy}px)`;
        dotRef.current.style.opacity = visible ? 1 : 0;
      }

      // Ring — lags behind, scales up on hover/click
      if (ringRef.current) {
        const scale = clicking ? 0.75 : hovering ? 1.5 : 1;
        ringRef.current.style.transform = `translate(${rx}px, ${ry}px) scale(${scale})`;
        ringRef.current.style.opacity = visible ? (hovering ? 0.9 : 0.55) : 0;
      }

      // Crosshair arms — extend from ring center
      const armLen = clicking ? 16 : hovering ? 28 : 20;
      const gap    = clicking ? 5  : hovering ? 8  : 6;

      if (cross1Ref.current) {
        // Horizontal
        cross1Ref.current.style.left   = `${rx - armLen}px`;
        cross1Ref.current.style.top    = `${ry}px`;
        cross1Ref.current.style.width  = `${(armLen - gap) * 2}px`;
        cross1Ref.current.style.opacity = visible ? (hovering ? 0.9 : 0.4) : 0;
      }

      if (cross2Ref.current) {
        // Vertical
        cross2Ref.current.style.left    = `${rx}px`;
        cross2Ref.current.style.top     = `${ry - armLen}px`;
        cross2Ref.current.style.height  = `${(armLen - gap) * 2}px`;
        cross2Ref.current.style.opacity = visible ? (hovering ? 0.9 : 0.4) : 0;
      }

      raf.current = requestAnimationFrame(animate);
    };

    raf.current = requestAnimationFrame(animate);

    return () => {
      document.body.style.cursor = '';
      document.removeEventListener('mousemove', move);
      document.removeEventListener('mouseover', over);
      document.removeEventListener('mousedown', down);
      document.removeEventListener('mouseup', up);
      document.removeEventListener('mouseleave', leave);
      document.removeEventListener('mouseenter', enter);
      cancelAnimationFrame(raf.current);
    };
  }, [visible, hovering, clicking]);

  return (
    <>
      {/* Crosshair horizontal arm */}
      <div ref={cross1Ref} className={`${styles.arm} ${styles.armH} ${hovering ? styles.armHover : ''}`} />

      {/* Crosshair vertical arm */}
      <div ref={cross2Ref} className={`${styles.arm} ${styles.armV} ${hovering ? styles.armHover : ''}`} />

      {/* Lagging ring */}
      <div ref={ringRef}
        className={`${styles.ring} ${hovering ? styles.ringHover : ''} ${clicking ? styles.ringClick : ''}`}
      />

      {/* Sharp center dot — follows exactly */}
      <div ref={dotRef}
        className={`${styles.dot} ${hovering ? styles.dotHover : ''}`}
      />
    </>
  );
}
